import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiMapPin, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../../provider/auth.provider';
import { checkIn, checkOut } from '../../libs/attendance';
import { toast } from 'react-hot-toast';

interface AttendanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'check-in' | 'check-out';
}

const AttendanceModal: React.FC<AttendanceModalProps> = ({ isOpen, onClose, type }) => {
    const { fetchAttendance } = useAuth();
    const [step, setStep] = useState<1 | 2>(1); // 1: Location, 2: Remarks
    const [location, setLocation] = useState<{ lat: number; lng: number; address: string } | null>(null);
    const [remarks, setRemarks] = useState('');
    const [loading, setLoading] = useState(false);
    // True while the reverse geocode is still in flight, so the address line can show a
    // spinner instead of a stale placeholder.
    const [addressLoading, setAddressLoading] = useState(false);
    // Selfie capture is disabled by requirement.
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (isOpen && step === 1) {
            getLocation();
        }
    }, [isOpen, step]);

    const getLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocation is not supported by your browser");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                if (latitude < 6 || latitude > 38 || longitude < 68 || longitude > 98) {
                    toast.error("Current location is outside the allowed India coordinate range.");
                    setLocation(null);
                    return;
                }
                setLocation({
                    lat: latitude,
                    lng: longitude,
                    address: ""
                });
                setAddressLoading(true);

                // The address is resolved by Google's Geocoding API, but always through
                // our own /maps/reverse-geocode proxy so the API key stays server-side.
                // Coordinates are the fallback whenever the lookup fails.
                const token = localStorage.getItem('token') || '';
                const coordsFallback = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
                try {
                    const geocodeRes = await fetch('/api/maps/reverse-geocode', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ lat: latitude, lng: longitude })
                    });

                    if (geocodeRes.ok) {
                        const data = await geocodeRes.json();
                        const address = data?.data?.formatted_address || data?.data?.address || coordsFallback;
                        setLocation(prev => prev ? { ...prev, address } : null);
                    } else {
                        setLocation(prev => prev ? { ...prev, address: coordsFallback } : null);
                    }
                } catch (err) {
                    console.error("Error fetching location data:", err);
                    setLocation(prev => prev ? { ...prev, address: coordsFallback } : null);
                } finally {
                    setAddressLoading(false);
                }
            },
            (err) => {
                console.error("Error getting location:", err);
                toast.error("Could not get location. Please ensure permissions are granted.");
            }
        );
    };

    const handleSubmit = async () => {
        if (!location) {
            toast.error("Location is required.");
            return;
        }

        setLoading(true);
        // If the geocode never resolved, store the coordinates rather than an empty address.
        const address = location.address || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
        try {
            const formData = new FormData();
            if (type === 'check-in') {
                formData.append('check_in_latitude', location.lat.toString());
                formData.append('check_in_longitude', location.lng.toString());
                formData.append('check_in_address', address);
                formData.append('user_remarks', remarks);
                // formData.append('check_in_selfie', file); // Disabled: selfie is no longer required.
                await checkIn(formData);
            } else {
                formData.append('check_out_latitude', location.lat.toString());
                formData.append('check_out_longitude', location.lng.toString());
                formData.append('check_out_address', address);
                formData.append('user_remarks', remarks);
                // formData.append('check_out_selfie', file); // Disabled: selfie is no longer required.
                await checkOut(formData);
            }

            toast.success(`${type === 'check-in' ? 'Checked in' : 'Checked out'} successfully!`);
            await fetchAttendance();
            onClose();
            // Reset state
            setStep(1);
            setLocation(null);
            setRemarks('');
        } catch (err: any) {
            console.error("Error during attendance:", err);
            // Error toast is already handled by axios interceptor if it returns a response
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 md:p-8">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
            <div className="relative bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900 flex items-center justify-between sticky top-0 z-10">
                    <h2 className="text-lg sm:text-xl font-semibold text-white capitalize">
                        {type.replace('-', ' ')}
                    </h2>
                    <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
                        <FiX className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                </div>

                <div className="p-4 sm:p-6">
                    {step === 1 ? (
                        <div className="space-y-4">
                            {/* Selfie capture UI intentionally disabled. */}
                            <video ref={videoRef} className="hidden" />
                            <canvas ref={canvasRef} className="hidden" />

                            {/* Address only — the map preview was removed by requirement.
                                The text comes from Google Geocoding via our own server proxy. */}
                            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                <div className="p-3 sm:p-4 flex items-start gap-3">
                                    <FiMapPin className="w-5 h-5 text-blue-800 mt-1 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-gray-700">Your Location</p>
                                        {!location ? (
                                            <p className="text-xs text-gray-500 mt-1">Accessing location...</p>
                                        ) : addressLoading ? (
                                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                                <span className="w-3 h-3 border-2 border-blue-800 border-t-transparent rounded-full animate-spin" />
                                                Fetching address...
                                            </p>
                                        ) : (
                                            <p className="text-sm text-gray-800 mt-1 break-words leading-relaxed">
                                                {location.address}
                                            </p>
                                        )}
                                        {location && (
                                            <p className="text-[10px] text-gray-400 mt-1.5">
                                                {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => setStep(2)}
                                disabled={!location || addressLoading}
                                className={`w-full py-2.5 sm:py-3 rounded-lg font-semibold text-white transition-all text-sm sm:text-base ${(!location || addressLoading) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-800 hover:bg-blue-900 shadow-md'
                                    }`}
                            >
                                Next Step
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                                <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors h-32 resize-none text-sm sm:text-base"
                                    placeholder="Enter your remarks here..."
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setStep(1)}
                                    className="flex-1 py-2.5 sm:py-3 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all border border-gray-300 text-sm sm:text-base"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="flex-1 py-2.5 sm:py-3 rounded-lg font-semibold text-white bg-green-600 hover:bg-green-700 transition-all shadow-md flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <FiCheckCircle className="w-5 h-5" />
                                            Submit
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttendanceModal;
